import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const AccessManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [approverUsers, setApproverUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [roleLoading, setRoleLoading] = useState(false);
  const [roleError, setRoleError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');

  useEffect(() => {
    fetchApproverUsers();
    fetchAvailableRoles();
  }, []);

  const fetchApproverUsers = async () => {
    try {
      const response = await axios.get('/api/admin/users-with-role/SYSTEM_APPROVER');
      setApproverUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch approver users:', error);
      setError('Failed to load approver users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableRoles = async () => {
    try {
      const response = await axios.get('/api/admin/system-roles');
      setAvailableRoles(response.data);
    } catch (error) {
      console.error('Failed to fetch available roles:', error);
    }
  };

  const searchUsers = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await axios.get(`/api/admin/search-users?query=${encodeURIComponent(query)}`);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Failed to search users:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    searchUsers(query);
  };

  const handleUserSelect = async (selectedUser) => {
    setSelectedUser(selectedUser);
    setSearchQuery(selectedUser.fullName);
    setSearchResults([]);
    
    // Fetch user's current roles
    try {
      const response = await axios.get(`/api/admin/user-roles/${selectedUser.id}`);
      setUserRoles(response.data);
      setShowRoleModal(true);
    } catch (error) {
      console.error('Failed to fetch user roles:', error);
      setError('Failed to load user roles. Please try again.');
    }
  };

  const handleRoleToggle = (roleCode) => {
    const hasRole = userRoles.some(role => role.code === roleCode);
    
    if (hasRole) {
      // Remove role (but ensure at least one role remains)
      if (userRoles.length <= 1) {
        setRoleError('User must have at least one system role');
        return;
      }
      setUserRoles(userRoles.filter(role => role.code !== roleCode));
    } else {
      // Add role
      const roleToAdd = availableRoles.find(role => role.code === roleCode);
      if (roleToAdd) {
        setUserRoles([...userRoles, roleToAdd]);
      }
    }
    setRoleError('');
  };

  const handleSaveRoles = async () => {
    if (userRoles.length === 0) {
      setRoleError('User must have at least one system role');
      return;
    }

    setRoleLoading(true);
    setRoleError('');

    try {
      const roleIds = userRoles.map(role => role.id);
      await axios.put(`/api/admin/user-roles/${selectedUser.id}`, { roleIds });
      
      setShowRoleModal(false);
      setConfirmMessage(`Roles updated successfully for ${selectedUser.fullName}`);
      setShowConfirmModal(true);
      
      // Refresh approver users list if the updated user has SYSTEM_APPROVER role
      if (userRoles.some(role => role.code === 'SYSTEM_APPROVER')) {
        fetchApproverUsers();
      }
    } catch (error) {
      console.error('Failed to update user roles:', error);
      setRoleError(error.response?.data?.message || 'Failed to update roles. Please try again.');
    } finally {
      setRoleLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'var(--success)';
      case 'INACTIVE': return 'var(--warning)';
      case 'BLOCKED': return 'var(--error)';
      default: return 'var(--gray-dark)';
    }
  };

  return (
    <div className="page">
      <h1 className="page-title">Access Management</h1>
      
      <div style={{
        marginBottom: '20px',
        padding: '15px',
        background: 'linear-gradient(135deg, #4A90E2 0%, #2C5282 100%)',
        color: 'white',
        borderRadius: '8px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '5px' }}>
          🔑 System Administrator Access
        </div>
        <div style={{ fontSize: '14px', opacity: '0.9' }}>
          Manage user system roles and permissions across the entire platform
        </div>
      </div>

      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      {/* User Search Section */}
      <div className="card" style={{ marginBottom: '30px' }}>
        <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
          Search and Manage User Roles
        </h3>
        
        <div style={{ position: 'relative', marginBottom: '20px' }}>
          <label htmlFor="userSearch" className="form-label">
            Search User by Name or Email
          </label>
          <input
            id="userSearch"
            type="text"
            className="form-input"
            placeholder="Type user name or email to search..."
            value={searchQuery}
            onChange={handleSearchChange}
            style={{ paddingRight: searchLoading ? '40px' : '12px' }}
          />
          {searchLoading && (
            <div style={{
              position: 'absolute',
              right: '12px',
              top: '32px',
              color: 'var(--primary-blue)'
            }}>
              ⏳
            </div>
          )}
          
          {/* Search Results Dropdown */}
          {searchResults.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: '0',
              right: '0',
              background: 'var(--white)',
              border: '1px solid var(--gray-medium)',
              borderRadius: '6px',
              boxShadow: '0 4px 15px rgba(74, 144, 226, 0.2)',
              zIndex: 1000,
              maxHeight: '300px',
              overflowY: 'auto'
            }}>
              {searchResults.map((searchUser) => (
                <div
                  key={searchUser.id}
                  onClick={() => handleUserSelect(searchUser)}
                  style={{
                    padding: '12px',
                    borderBottom: '1px solid var(--gray-light)',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--pale-blue)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  <div style={{ fontWeight: '500', color: 'var(--dark-blue)' }}>
                    {searchUser.fullName}
                    <span style={{ 
                      marginLeft: '8px', 
                      fontSize: '10px', 
                      background: 'var(--primary-blue)', 
                      color: 'white', 
                      padding: '2px 6px', 
                      borderRadius: '10px' 
                    }}>
                      ID: {searchUser.id}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                    {searchUser.email} • {searchUser.memberRole} • {searchUser.status}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--primary-blue)', marginTop: '2px' }}>
                    Current Roles: {searchUser.systemRoles || 'None'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div style={{ 
          fontSize: '14px', 
          color: 'var(--gray-dark)',
          background: 'var(--pale-blue)',
          padding: '10px',
          borderRadius: '6px'
        }}>
          💡 Search for any user in the system to manage their system roles and permissions
        </div>
      </div>

      {/* System Approvers Section */}
      <div className="card">
        <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
          Users with SYSTEM_APPROVER Role ({approverUsers.length} total)
        </h3>
        
        {loading ? (
          <div className="loading">Loading approver users...</div>
        ) : approverUsers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
            No users found with SYSTEM_APPROVER role.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Full Name
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Email
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Member Role
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    All System Roles
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Status
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'center',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {approverUsers.map((approverUser, index) => (
                  <tr 
                    key={approverUser.id}
                    style={{ 
                      backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                      borderBottom: '1px solid var(--gray-medium)'
                    }}
                  >
                    <td style={{ padding: '12px', fontWeight: '500' }}>
                      {approverUser.fullName || 'N/A'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {approverUser.email}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        background: 'var(--primary-blue)',
                        color: 'var(--white)',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        {approverUser.memberRole || 'No Role'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {approverUser.systemRoles?.split(', ').map((role, idx) => (
                          <span 
                            key={idx}
                            style={{
                              background: role === 'SYSTEM_APPROVER' ? 'var(--success)' : 'var(--gray-medium)',
                              color: role === 'SYSTEM_APPROVER' ? 'white' : 'var(--gray-dark)',
                              padding: '2px 6px',
                              borderRadius: '3px',
                              fontSize: '11px'
                            }}
                          >
                            {role}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        color: getStatusColor(approverUser.status),
                        fontWeight: '500'
                      }}>
                        {approverUser.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <button 
                        className="btn btn-primary"
                        style={{ fontSize: '11px', padding: '4px 8px' }}
                        onClick={() => handleUserSelect(approverUser)}
                        title="Manage Roles"
                      >
                        Manage Roles
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Role Management Modal */}
      {showRoleModal && selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
                Manage System Roles
              </h3>
              <button
                onClick={() => {
                  setShowRoleModal(false);
                  setSelectedUser(null);
                  setRoleError('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--gray-dark)'
                }}
              >
                ×
              </button>
            </div>

            {roleError && (
              <div className="alert alert-error">{roleError}</div>
            )}

            {/* User Information */}
            <div style={{
              background: 'var(--pale-blue)',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <h4 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>
                User Information
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px' }}>
                <div><strong>Name:</strong> {selectedUser.fullName}</div>
                <div><strong>Email:</strong> {selectedUser.email}</div>
                <div><strong>Member Role:</strong> {selectedUser.memberRole || 'No Role'}</div>
                <div><strong>Status:</strong> 
                  <span style={{
                    color: getStatusColor(selectedUser.status),
                    marginLeft: '5px',
                    fontWeight: '500'
                  }}>
                    {selectedUser.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Role Selection */}
            <div>
              <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
                System Roles (Select at least one)
              </h4>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {availableRoles.filter(role => role.code !== 'SUPER_ADMIN').map((role) => {
                  const isSelected = userRoles.some(userRole => userRole.code === role.code);
                  const isLastRole = userRoles.length === 1 && isSelected;
                  
                  return (
                    <label 
                      key={role.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '12px',
                        border: `2px solid ${isSelected ? 'var(--primary-blue)' : 'var(--gray-medium)'}`,
                        borderRadius: '8px',
                        cursor: isLastRole ? 'not-allowed' : 'pointer',
                        background: isSelected ? 'var(--pale-blue)' : 'var(--white)',
                        opacity: isLastRole ? 0.6 : 1
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleRoleToggle(role.code)}
                        disabled={isLastRole}
                        style={{ cursor: isLastRole ? 'not-allowed' : 'pointer' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500', color: 'var(--dark-blue)' }}>
                          {role.displayName}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                          Code: {role.code}
                        </div>
                      </div>
                      {isLastRole && (
                        <div style={{ 
                          fontSize: '11px', 
                          color: 'var(--error)',
                          fontStyle: 'italic'
                        }}>
                          Cannot remove last role
                        </div>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', marginTop: '30px' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowRoleModal(false);
                  setSelectedUser(null);
                  setRoleError('');
                }}
                disabled={roleLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveRoles}
                disabled={roleLoading || userRoles.length === 0}
              >
                {roleLoading ? 'Saving...' : 'Save Roles'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '400px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
              Success!
            </h3>
            <p style={{ color: 'var(--gray-dark)', marginBottom: '25px' }}>
              {confirmMessage}
            </p>
            <button
              className="btn btn-primary"
              onClick={() => setShowConfirmModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessManagement;
